import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../lib/auth.context'
import Background from '../background'
import StepsIndicator from '../steps-indicator'
import { InputOTP } from '../input-otp'
import { EnrollmentSuccess } from './totp-enrollment-success'

interface TOTPEnrollmentProps {
  onComplete?: () => void
  redirectTo?: string
}

export function TOTPEnrollment({ onComplete, redirectTo }: TOTPEnrollmentProps) {
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation()
  const { setIsTOTPVerified } = useAuth()
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    const setupTOTP = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user?.email) throw new Error('User email not found')

        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `${t('title')} - ${user.email}`,
        })

        if (error) throw error

        if (data.totp) {
          setQrCode(data.totp.qr_code)
          setSecret(data.totp.secret)
          setFactorId(data.id)
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to setup TOTP'
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    setupTOTP()
  }, [toast, t])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId,
      })

      if (error) throw error

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode,
      })

      if (verifyError) throw verifyError

      toast({
        title: 'Success',
        description: 'TOTP device enrolled successfully',
      })

      // Refresh the session to ensure we have the latest token
      const { data: { session: newSession }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      // Ensure we have a valid session before redirecting
      if (!newSession?.access_token) {
        throw new Error('Failed to initialize session after TOTP enrollment')
      }

      // Set TOTP verification status after successful enrollment
      console.log('[TOTPEnrollment] Setting TOTP verification to true after successful enrollment')
      setIsTOTPVerified(true)
      
      setShowSuccess(true)
     
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Background>
      <div className={`flex min-h-screen items-center justify-center`}>
        {!showSuccess ? (
          <form onSubmit={handleVerify}>
            <Card className="w-full max-w-lg border border-white rounded-3xl bg-white/50 p-8 justify-center">
              <div className="flex flex-col w-full gap-6 items-center">
                <div className="flex flex-col items-center mx-8 gap-2">
                  <h1 className="font-semibold text-lavender-900 text-[40px] leading-[52px]">Setup 2FA</h1>
                  <div className="flex flex-col">
                    <p className="font-[400] text-[14px]  text-[#666666]">
                      You need an authentication app to scan the QR below
                    </p>
                    <p className="font-[400] text-[14px] text-[#666666]">
                      (Google Authenticator, Microsoft Authenticator, etc).
                    </p>
                  </div>
                </div>
                <StepsIndicator currentStep={2} />
                <div className="flex flex-col mt-4 items-center gap-8 border border-white rounded-3xl h-36 w-36">
                  {qrCode && (
                    <img src={qrCode} alt="QR Code" className="w-full h-full" />
                  )}
                </div>
                <p className="text-lavender-900 font-normal leading-relaxed text-sm">
                  Enter the 6-digit authentication code generated by the app
                </p>
                <InputOTP length={6} onChange={(value) => setVerifyCode(value)} />
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-3xl bg-[#354297] hover:bg-[#354297]"
                >
                  Enable 2FA
                </Button>
              </div>
            </Card>
          </form>
        ) : (
          <EnrollmentSuccess />
        )}
      </div>
    </Background>
    // <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
    //   <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>
    //     Set Up Two-Factor Authentication
    //   </h1>
    //   <div className="space-y-4">
    //     <div className="text-center">
    //       {qrCode && (
    //         <div className="mb-4">
    //           <img src={qrCode} alt="QR Code" className="mx-auto" />
    //         </div>
    //       )}
    //       {secret && (
    //         <div className="mb-4">
    //           <p className="text-sm text-gray-600">Manual entry code:</p>
    //           <code className="block p-2 bg-gray-100 rounded">{secret}</code>
    //         </div>
    //       )}
    //       <p className={`mb-4 ${getThemeClass('components.text.body')}`}>
    //         Scan the QR code or enter the secret key manually in your authenticator app
    //       </p>
    //     </div>
    //     <form onSubmit={handleVerify} className="space-y-4">
    //       <div>
    //         <Input
    //           type="text"
    //           placeholder="Enter verification code"
    //           value={verifyCode}
    //           onChange={(e) => setVerifyCode(e.target.value)}
    //           required
    //           disabled={isLoading}
    //           className={getThemeClass('components.input.base')}
    //         />
    //       </div>
    //       {formError && (
    //         <Alert variant="destructive" className="mt-4">
    //           <AlertDescription>{formError}</AlertDescription>
    //         </Alert>
    //       )}
    //       <Button
    //         type="submit"
    //         className={`w-full ${getThemeClass('components.button.primary')}`}
    //         disabled={isLoading}
    //       >
    //         {isLoading ? 'Verifying...' : 'Verify'}
    //       </Button>
    //     </form>
    //   </div>
    // </Card>
  )
}
